import { useState } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import api, { apiErr } from "../lib/api";
import { Shield, RotateCcw, Zap, CheckCircle2, Mail, Send, AlertTriangle } from "lucide-react";
import BrokerConnectors from "./BrokerConnectors";

const PROFILES = ["Conservative", "Balanced", "Dynamic", "Custom"];
const CAPITALS = [15, 50, 100, 500, 1000, 10000, 100000];

function Card({ title, icon, children }) {
  return (
    <div className="border border-[#1E2028] bg-[#0F1015] p-4">
      <div className="flex items-center gap-2 text-sm font-display font-bold mb-3">{icon}{title}</div>
      {children}
    </div>
  );
}

export default function SettingsPanel({ account, onUpdate }) {
  const { t } = useLang();
  const [profile, setProfile] = useState(account?.risk_profile || "Balanced");
  const [custom, setCustom] = useState(account?.limits || { risk_per_trade: 1, daily_loss: 3, max_drawdown: 10, max_positions: 3 });
  const [cap, setCap] = useState(account?.start_capital || 500);
  const [emailOn, setEmailOn] = useState(!!account?.email_alerts);
  const [alertEmail, setAlertEmail] = useState(account?.alert_email || "");
  const [emailBusy, setEmailBusy] = useState(false);

  const saveEmail = async () => {
    setEmailBusy(true);
    try {
      const { data } = await api.put("/account/email-alerts", { enabled: emailOn, alert_email: alertEmail });
      onUpdate(data); toast.success(t("saveAlerts") + " ✓");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setEmailBusy(false); }
  };
  const testEmail = async () => {
    setEmailBusy(true);
    try { const { data } = await api.post("/account/email-alerts/test"); toast.success(`${t("sendTest")} ✓ → ${data.sent_to}`); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setEmailBusy(false); }
  };
  const testDigest = async () => {
    setEmailBusy(true);
    try { const { data } = await api.post("/account/email-digest/test"); toast.success(`${t("sendDigest")} ✓ → ${data.sent_to}`); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setEmailBusy(false); }
  };

  const applyRisk = async () => {
    try {
      const { data } = await api.put("/account/risk", { risk_profile: profile, custom_limits: profile === "Custom" ? custom : null });
      onUpdate(data); toast.success(t("apply") + " ✓");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const reset = async () => {
    try { const { data } = await api.post("/account/reset", { start_capital: Number(cap) }); onUpdate(data); toast.success(t("resetAccount") + " ✓"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleLive = async () => {
    try { const { data } = await api.post("/account/live"); onUpdate(); toast[data.live_enabled ? "warning" : "info"](data.live_enabled ? t("liveActive") : t("liveTrading")); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const limits = account?.limits || {};

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card title={t("riskProfile")} icon={<Shield className="w-4 h-4 text-[#FACC15]" />}>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {PROFILES.map((p) => (
            <button key={p} data-testid={`risk-${p}`} onClick={() => setProfile(p)}
              className={`py-2 text-xs font-semibold transition-colors duration-200 border ${
                profile === p ? "bg-[#FACC15] text-[#05050A] border-[#FACC15]" : "border-[#1E2028] text-[#9CA3AF] hover:border-[#4B5563]"}`}>
              {p}
            </button>
          ))}
        </div>
        {profile === "Custom" ? (
          <div className="space-y-3">
            {[["risk_per_trade", t("riskPerTrade"), "%"], ["daily_loss", t("dailyLoss"), "%"], ["max_drawdown", t("maxDrawdown"), "%"], ["max_positions", t("maxPositions"), ""]].map(([k, label, unit]) => (
              <label key={k} className="flex items-center justify-between text-xs">
                <span className="text-[#9CA3AF]">{label}</span>
                <span className="flex items-center gap-1">
                  <input type="number" value={custom[k]} data-testid={`custom-${k}`}
                    onChange={(e) => setCustom({ ...custom, [k]: Number(e.target.value) })}
                    className="w-20 bg-[#05050A] border border-[#1E2028] px-2 py-1 font-mono text-right focus:border-[#FACC15] focus:outline-none" />
                  <span className="text-[#6B7280] w-3">{unit}</span>
                </span>
              </label>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 text-xs font-mono">
            <Info label={t("riskPerTrade")} value={`${limits.risk_per_trade}%`} />
            <Info label={t("dailyLoss")} value={`${limits.daily_loss}%`} />
            <Info label={t("maxDrawdown")} value={`${limits.max_drawdown}%`} />
            <Info label={t("maxPositions")} value={limits.max_positions} />
          </div>
        )}
        <button data-testid="apply-risk" onClick={applyRisk}
          className="mt-4 w-full bg-[#FACC15] text-[#05050A] font-semibold py-2 text-xs hover:bg-[#eab308] transition-colors duration-200">{t("apply")}</button>
      </Card>

      <Card title={t("resetAccount")} icon={<RotateCcw className="w-4 h-4 text-[#FACC15]" />}>
        <div className="text-xs text-[#9CA3AF] mb-2 font-mono">{t("startCapital")}</div>
        <div className="grid grid-cols-4 gap-2 mb-3">
          {CAPITALS.map((c) => (
            <button key={c} onClick={() => setCap(c)} data-testid={`capital-${c}`}
              className={`py-2 text-xs font-mono transition-colors duration-200 border ${cap === c ? "bg-[#FACC15] text-[#05050A] border-[#FACC15]" : "border-[#1E2028] text-[#9CA3AF] hover:border-[#4B5563]"}`}>
              €{c >= 1000 ? `${c / 1000}k` : c}
            </button>
          ))}
        </div>
        <input type="number" value={cap} onChange={(e) => setCap(e.target.value)} data-testid="capital-input"
          className="w-full bg-[#05050A] border border-[#1E2028] px-3 py-2 font-mono text-sm mb-3 focus:border-[#FACC15] focus:outline-none" />
        {Number(cap) < 200 && (
          <div className="text-[10px] text-[#F59E0B] font-mono mb-3 border border-[#F59E0B]/30 bg-[#F59E0B]/10 p-2">
            <span className="font-bold">{t("microCapital")}</span> — {t("microDesc")}
          </div>
        )}
        <button data-testid="reset-account" onClick={reset}
          className="w-full border border-[#EF4444] text-[#EF4444] font-semibold py-2 text-xs hover:bg-[#EF4444]/10 transition-colors duration-200">{t("resetAccount")}</button>
      </Card>

      <Card title={t("liveTrading")} icon={<Zap className="w-4 h-4 text-[#8B5CF6]" />}>
        <p className="text-xs text-[#9CA3AF] leading-relaxed mb-3">{t("liveWarning")}</p>
        <div className="flex items-center gap-2 mb-4">
          {["Demo Test", "Backtest", "Forward Test"].map((s) => (
            <span key={s} className="flex items-center gap-1 text-[10px] font-mono text-[#10B981]"><CheckCircle2 className="w-3 h-3" /> {s}</span>
          ))}
        </div>
        <button data-testid="toggle-live" onClick={toggleLive}
          className={`w-full font-bold py-2.5 text-xs uppercase tracking-wide transition-colors duration-200 border ${
            account?.live_enabled ? "bg-[#8B5CF6] text-white border-[#8B5CF6]" : "border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6]/10"}`}>
          {account?.live_enabled ? t("liveActive") : t("activateLive")}
        </button>
      </Card>

      <Card title={t("emailAlerts")} icon={<Mail className="w-4 h-4 text-[#FACC15]" />}>
        <p className="text-xs text-[#9CA3AF] leading-relaxed mb-3">{t("emailAlertsDesc")}</p>
        {!account?.email_configured && (
          <div className="flex items-start gap-1.5 text-[10px] text-[#EF4444] font-mono mb-3 border border-[#EF4444]/30 bg-[#EF4444]/10 p-2">
            <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" /> {t("emailNotConfigured")}
          </div>
        )}
        <label className="flex items-center justify-between mb-3 cursor-pointer">
          <span className="text-xs text-[#D1D5DB]">{t("enableEmailAlerts")}</span>
          <button type="button" data-testid="toggle-email-alerts" onClick={() => setEmailOn(!emailOn)}
            className={`w-11 h-6 rounded-full transition-colors duration-200 relative ${emailOn ? "bg-[#FACC15]" : "bg-[#1E2028]"}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all duration-200 ${emailOn ? "left-[22px]" : "left-0.5"}`} />
          </button>
        </label>
        <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono mb-1">{t("alertEmail")}</div>
        <input type="email" value={alertEmail} onChange={(e) => setAlertEmail(e.target.value)} data-testid="alert-email-input"
          placeholder="you@example.com"
          className="w-full bg-[#05050A] border border-[#1E2028] px-3 py-2 font-mono text-sm mb-3 focus:border-[#FACC15] focus:outline-none" />
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="save-email-alerts" onClick={saveEmail} disabled={emailBusy}
            className="bg-[#FACC15] text-[#05050A] font-semibold py-2 text-xs hover:bg-[#eab308] transition-colors duration-200 disabled:opacity-50">
            {t("saveAlerts")}
          </button>
          <button data-testid="test-email-alert" onClick={testEmail} disabled={emailBusy || !alertEmail || !account?.email_configured}
            className="flex items-center justify-center gap-1.5 border border-[#1E2028] text-[#9CA3AF] py-2 text-xs hover:border-[#FACC15] hover:text-[#FACC15] transition-colors duration-200 disabled:opacity-40">
            <Send className="w-3 h-3" /> {t("sendTest")}
          </button>
        </div>
        <button data-testid="test-digest" onClick={testDigest} disabled={emailBusy || !alertEmail || !account?.email_configured}
          className="mt-2 w-full flex items-center justify-center gap-1.5 border border-[#1E2028] text-[#9CA3AF] py-2 text-xs hover:border-[#FACC15] hover:text-[#FACC15] transition-colors duration-200 disabled:opacity-40">
          <Mail className="w-3 h-3" /> {t("sendDigest")}
        </button>
        <p className="mt-2 text-[10px] text-[#6B7280] font-mono">{t("emailVerifyNote")}</p>
      </Card>

      </div>
      <BrokerConnectors />
    </div>
  );
}

const Info = ({ label, value }) => (
  <div className="border border-[#1E2028] bg-[#05050A] p-2">
    <div className="text-[9px] uppercase text-[#6B7280]">{label}</div>
    <div className="text-white">{value}</div>
  </div>
);
